import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api, { labAPI, documentAPI } from '../../lib/api';
import { 
  Flask,
  MagnifyingGlass,
  Plus,
  Eye,
  X,
  Upload,
  Sparkle,
  ArrowClockwise,
  CheckCircle,
  Warning,
  TrendUp,
  TrendDown,
  Minus,
  FileText,
  CalendarBlank,
  User,
  Buildings,
  ChartLine
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import LabTrendsChart from '../../components/charts/LabTrendsChart';

export default function LabResultsPage() {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [labResults, setLabResults] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('results'); // 'results' or 'trends'
  
  // Documents for extraction
  const [labDocuments, setLabDocuments] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  
  // AI Analysis
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Manual entry modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLab, setNewLab] = useState({
    panel_name: '',
    lab_name: '',
    collection_date: '',
    results: []
  });
  
  // View modal
  const [selectedLab, setSelectedLab] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const fetchLabResults = useCallback(async () => {
    if (!selectedPatient) return;
    setLoading(true);
    try {
      const response = await labAPI.getPatientLabs(selectedPatient.id);
      setLabResults(response.data.lab_results || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching lab results:', error);
      toast.error('Failed to load lab results');
    } finally {
      setLoading(false);
    }
  }, [selectedPatient]);

  const fetchLabDocuments = useCallback(async () => {
    if (!selectedPatient) return;
    try {
      const response = await documentAPI.getPatientDocs(selectedPatient.id);
      const labDocs = (response.data || []).filter(doc => doc.document_type === 'lab_result');
      setLabDocuments(labDocs);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching lab documents:', error);
    }
  }, [selectedPatient]);

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data.patients || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching patients:', error);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      fetchLabResults();
      fetchLabDocuments();
    }
  }, [selectedPatient, fetchLabResults, fetchLabDocuments]);

  const extractFromDocument = async (documentId) => {
    setExtracting(true);
    setExtractedData(null);
    try {
      const response = await labAPI.extract(documentId);
      setExtractedData(response.data);
      toast.success('Lab values extracted');
      
      // Pre-fill the form if extraction was successful
      if (response.data.extracted_data && !response.data.extracted_data.error) {
        const data = response.data.extracted_data;
        setNewLab({
          panel_name: data.panel_name || '',
          lab_name: data.lab_name || '',
          collection_date: data.collection_date || '',
          results: data.results || []
        });
        setShowAddModal(true);
      }
    } catch (error) {
      toast.error('Extraction failed');
      process.env.NODE_ENV === "development" && console.error(error);
    } finally {
      setExtracting(false);
    }
  };

  const runAIAnalysis = async () => {
    if (!selectedPatient) return;
    setAnalyzing(true);
    try {
      const response = await labAPI.aiAnalyze(selectedPatient.id);
      setAiAnalysis(response.data.analysis);
      toast.success('AI analysis complete');
    } catch (error) {
      toast.error('Analysis failed');
      process.env.NODE_ENV === "development" && console.error(error);
    } finally {
      setAnalyzing(false);
    }
  };

  const saveLab = async () => {
    if (!selectedPatient || !newLab.panel_name) {
      toast.error('Please enter panel name');
      return;
    }
    
    try {
      await labAPI.create({
        patient_id: selectedPatient.id,
        ...newLab
      });
      toast.success('Lab result saved');
      setShowAddModal(false);
      setNewLab({ panel_name: '', lab_name: '', collection_date: '', results: [] });
      fetchLabResults();
    } catch (error) {
      toast.error('Failed to save lab result');
    }
  };

  const reviewLab = async (labId) => {
    try {
      await labAPI.review(labId);
      toast.success('Lab result marked as reviewed');
      fetchLabResults();
    } catch (error) {
      toast.error('Failed to review lab');
    }
  };

  const addResultRow = () => {
    setNewLab({
      ...newLab,
      results: [...newLab.results, { test_name: '', value: '', unit: '', reference_range: '', flag: 'N' }]
    });
  };

  const updateResultRow = (index, field, value) => {
    const updated = [...newLab.results];
    updated[index] = { ...updated[index], [field]: value };
    setNewLab({ ...newLab, results: updated });
  };

  const removeResultRow = (index) => {
    setNewLab({
      ...newLab,
      results: newLab.results.filter((_, i) => i !== index)
    });
  };

  const filteredPatients = patients.filter(p =>
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(patientSearch.toLowerCase()) ||
    p.email.toLowerCase().includes(patientSearch.toLowerCase())
  );

  const getFlagColor = (flag) => {
    switch (flag) {
      case 'H': return 'text-red-600 bg-red-50';
      case 'L': return 'text-blue-600 bg-blue-50';
      case 'C': return 'text-red-700 bg-red-100 font-bold';
      default: return 'text-green-600 bg-green-50';
    }
  };

  const getFlagLabel = (flag) => {
    switch (flag) {
      case 'H': return 'High';
      case 'L': return 'Low';
      case 'C': return 'Critical';
      default: return 'Normal';
    }
  };

  return (
    <div className="p-8" data-testid="lab-results-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Lab Results
          </h1>
          <p className="text-slate-500">View, extract, and analyze patient lab results</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Patient Selection & Lab List */}
        <div className="lg:col-span-2 space-y-6">
          {/* Patient Selection */}
          <div className="card-clinical p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Select Patient</h2>
            <div className="relative mb-4">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
                placeholder="Search patients..."
                className="input-clinical pl-10"
                data-testid="patient-search-input"
              />
            </div>
            
            {selectedPatient ? (
              <div className="p-4 bg-teal-50 rounded-xl border border-teal-200 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">{selectedPatient.first_name} {selectedPatient.last_name}</p>
                  <p className="text-sm text-slate-500">{selectedPatient.email}</p>
                </div>
                <button 
                  onClick={() => { setSelectedPatient(null); setLabResults([]); setLabDocuments([]); setAiAnalysis(null); }} 
                  className="p-2 hover:bg-teal-100 rounded-lg"
                  data-testid="clear-patient-btn"
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto space-y-2" data-testid="patient-list">
                {filteredPatients.slice(0, 10).map((patient) => (
                  <button
                    key={patient.id}
                    onClick={() => setSelectedPatient(patient)}
                    className="w-full p-3 bg-slate-50 rounded-lg hover:bg-slate-100 text-left transition-colors"
                    data-testid={`patient-item-${patient.id}`}
                  >
                    <p className="font-medium text-slate-900">{patient.first_name} {patient.last_name}</p>
                    <p className="text-sm text-slate-500">{patient.email}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Tabs for Results vs Trends */}
          {selectedPatient && (
            <div className="flex border-b border-slate-200">
              <button
                onClick={() => setActiveTab('results')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'results'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
                data-testid="tab-results"
              >
                <Flask size={16} className="inline mr-2" />
                Lab Results
              </button>
              <button
                onClick={() => setActiveTab('trends')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'trends'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
                data-testid="tab-trends"
              >
                <ChartLine size={16} className="inline mr-2" />
                Trends Dashboard
              </button>
            </div>
          )}

          {/* Trends View */}
          {selectedPatient && activeTab === 'trends' && (
            <LabTrendsChart 
              labResults={labResults} 
              patientName={`${selectedPatient.first_name} ${selectedPatient.last_name}`}
            />
          )}

          {/* Lab Documents for Extraction */}
          {selectedPatient && activeTab === 'results' && labDocuments.length > 0 && (
            <div className="card-clinical p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Uploaded Lab Documents ({labDocuments.length})
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                Click "Extract" to use AI to parse lab values from uploaded documents
              </p>
              <div className="space-y-3">
                {labDocuments.map((doc) => (
                  <div key={doc.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText size={24} className="text-slate-400" />
                      <div>
                        <p className="font-medium text-slate-900">{doc.file_name}</p>
                        <p className="text-xs text-slate-500">
                          Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => extractFromDocument(doc.id)}
                      disabled={extracting}
                      className="btn-teal text-sm flex items-center gap-2"
                      data-testid={`extract-btn-${doc.id}`}
                    >
                      {extracting ? (
                        <ArrowClockwise size={16} className="animate-spin" />
                      ) : (
                        <Sparkle size={16} />
                      )}
                      Extract
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lab Results List */}
          {selectedPatient && activeTab === 'results' && (
            <div className="card-clinical p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Lab Results ({labResults.length})</h2>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="btn-teal flex items-center gap-2 text-sm"
                  data-testid="add-lab-btn"
                >
                  <Plus size={16} />
                  Add Manually
                </button>
              </div>

              {loading ? (
                <div className="p-8 text-center">
                  <ArrowClockwise size={24} className="animate-spin mx-auto text-teal-500" />
                </div>
              ) : labResults.length > 0 ? (
                <div className="space-y-4">
                  {labResults.map((lab) => (
                    <div key={lab.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Flask size={18} className="text-teal-500" />
                            <span className="font-medium text-slate-900">{lab.panel_name}</span>
                            {lab.status === 'reviewed' ? (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <CheckCircle size={12} /> Reviewed
                              </span>
                            ) : (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                Pending Review
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                            {lab.lab_name && (
                              <span className="flex items-center gap-1">
                                <Buildings size={14} /> {lab.lab_name}
                              </span>
                            )}
                            {lab.collection_date && (
                              <span className="flex items-center gap-1">
                                <CalendarBlank size={14} /> {lab.collection_date}
                              </span>
                            )}
                          </div>
                          
                          {/* Quick view of abnormal results */}
                          {lab.results && lab.results.filter(r => r.flag && r.flag !== 'N').length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {lab.results.filter(r => r.flag && r.flag !== 'N').slice(0, 4).map((r) => (
                                <span key={`${lab.id}-${r.test_name}-${r.value}`} className={`text-xs px-2 py-1 rounded ${getFlagColor(r.flag)}`}>
                                  {r.test_name}: {r.value} ({getFlagLabel(r.flag)})
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => { setSelectedLab(lab); setShowViewModal(true); }}
                            className="p-2 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg"
                            title="View Details"
                            data-testid={`view-lab-${lab.id}`}
                          >
                            <Eye size={18} />
                          </button>
                          {lab.status !== 'reviewed' && user?.role === 'provider' && (
                            <button
                              onClick={() => reviewLab(lab.id)}
                              className="p-2 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded-lg"
                              title="Mark as Reviewed"
                              data-testid={`review-lab-${lab.id}`}
                            >
                              <CheckCircle size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 bg-slate-50 rounded-xl text-center">
                  <Flask size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No lab results found</p>
                  <p className="text-sm text-slate-400 mt-1">Upload a lab document or add results manually</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column - AI Analysis */}
        <div className="space-y-6">
          {selectedPatient && (
            <div className="card-clinical p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkle size={20} className="text-amber-500" />
                  <h2 className="text-lg font-semibold text-slate-900">AI Lab Analysis</h2>
                </div>
                <button
                  onClick={runAIAnalysis}
                  disabled={analyzing || labResults.length === 0}
                  className="btn-teal text-sm flex items-center gap-2"
                  data-testid="ai-analyze-btn"
                >
                  {analyzing ? (
                    <ArrowClockwise size={16} className="animate-spin" />
                  ) : (
                    <Sparkle size={16} />
                  )}
                  {analyzing ? 'Analyzing...' : 'Analyze Labs'}
                </button>
              </div>

              {aiAnalysis ? (
                <div className="space-y-4">
                  {aiAnalysis.summary && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-700">{aiAnalysis.summary}</p>
                    </div>
                  )}
                  
                  {aiAnalysis.alerts?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-red-600 mb-2 flex items-center gap-1">
                        <Warning size={16} /> Alerts
                      </h4>
                      {aiAnalysis.alerts.map((alert, idx) => (
                        <div key={`alert-${idx}-${typeof alert === 'string' ? alert.substring(0, 20) : idx}`} className="p-2 bg-red-50 rounded border border-red-200 text-sm text-red-700 mb-2">
                          {typeof alert === 'string' ? alert : JSON.stringify(alert)}
                        </div>
                      ))}
                    </div>
                  )}

                  {aiAnalysis.trends?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-slate-700 mb-2">Trends</h4>
                      <div className="space-y-2">
                        {aiAnalysis.trends.map((trend) => (
                          <div key={`trend-${trend.metric}-${trend.direction}`} className="flex items-center gap-2 text-sm">
                            {trend.direction === 'improving' ? (
                              <TrendUp size={16} className="text-green-500" />
                            ) : trend.direction === 'worsening' ? (
                              <TrendDown size={16} className="text-red-500" />
                            ) : (
                              <Minus size={16} className="text-slate-400" />
                            )}
                            <span className="font-medium">{trend.metric}</span>
                            <span className="text-slate-500">{trend.note}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {aiAnalysis.recommendations?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-slate-700 mb-2">Recommendations</h4>
                      <ul className="space-y-1">
                        {aiAnalysis.recommendations.map((rec, idx) => (
                          <li key={`rec-${idx}-${typeof rec === 'string' ? rec.substring(0, 15) : idx}`} className="text-sm text-slate-600 flex items-start gap-2">
                            <CheckCircle size={14} className="text-teal-500 mt-0.5 flex-shrink-0" />
                            {typeof rec === 'string' ? rec : JSON.stringify(rec)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-slate-400 text-sm">
                    {labResults.length === 0 
                      ? 'Add lab results to enable AI analysis' 
                      : 'Click "Analyze Labs" for AI-powered insights'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Extracted Data Preview */}
          {extractedData && (
            <div className="card-clinical p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Extracted Data</h3>
              {extractedData.extracted_data?.error ? (
                <div className="p-3 bg-red-50 rounded-lg text-red-700 text-sm">
                  {extractedData.extracted_data.error}
                </div>
              ) : (
                <div className="space-y-2 text-sm">
                  <p><strong>Lab:</strong> {extractedData.extracted_data?.lab_name || 'N/A'}</p>
                  <p><strong>Panel:</strong> {extractedData.extracted_data?.panel_name || 'N/A'}</p>
                  <p><strong>Date:</strong> {extractedData.extracted_data?.collection_date || 'N/A'}</p>
                  <p><strong>Tests:</strong> {extractedData.extracted_data?.results?.length || 0}</p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="w-full btn-teal mt-3"
                  >
                    Edit & Save Results
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Lab Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Add Lab Result</h3>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-clinical">Panel Name *</label>
                  <input
                    type="text"
                    value={newLab.panel_name}
                    onChange={(e) => setNewLab({ ...newLab, panel_name: e.target.value })}
                    className="input-clinical"
                    placeholder="e.g., CBC, CMP, Lipid Panel"
                    data-testid="panel-name-input"
                  />
                </div>
                <div>
                  <label className="label-clinical">Lab Name</label>
                  <input
                    type="text"
                    value={newLab.lab_name}
                    onChange={(e) => setNewLab({ ...newLab, lab_name: e.target.value })}
                    className="input-clinical"
                    placeholder="e.g., LabCorp, Quest"
                  />
                </div>
              </div>

              <div>
                <label className="label-clinical">Collection Date</label>
                <input
                  type="date"
                  value={newLab.collection_date}
                  onChange={(e) => setNewLab({ ...newLab, collection_date: e.target.value })}
                  className="input-clinical"
                />
              </div>

              {/* Results Table */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label-clinical">Test Results</label>
                  <button
                    type="button"
                    onClick={addResultRow}
                    className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
                    data-testid="add-result-row-btn"
                  >
                    <Plus size={16} /> Add Row
                  </button>
                </div>
                
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {newLab.results.map((result, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <input
                        type="text"
                        value={result.test_name}
                        onChange={(e) => updateResultRow(index, 'test_name', e.target.value)}
                        className="input-clinical col-span-3 text-sm"
                        placeholder="Test name"
                      />
                      <input
                        type="text"
                        value={result.value}
                        onChange={(e) => updateResultRow(index, 'value', e.target.value)}
                        className="input-clinical col-span-2 text-sm"
                        placeholder="Value"
                      />
                      <input
                        type="text"
                        value={result.unit}
                        onChange={(e) => updateResultRow(index, 'unit', e.target.value)}
                        className="input-clinical col-span-2 text-sm"
                        placeholder="Unit"
                      />
                      <input
                        type="text"
                        value={result.reference_range}
                        onChange={(e) => updateResultRow(index, 'reference_range', e.target.value)}
                        className="input-clinical col-span-2 text-sm"
                        placeholder="Ref range"
                      />
                      <select
                        value={result.flag}
                        onChange={(e) => updateResultRow(index, 'flag', e.target.value)}
                        className="input-clinical col-span-2 text-sm"
                      >
                        <option value="N">Normal</option>
                        <option value="H">High</option>
                        <option value="L">Low</option>
                        <option value="C">Critical</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => removeResultRow(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg col-span-1"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                
                {newLab.results.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">
                    Click "Add Row" to add test results
                  </p>
                )}
              </div>

              <button onClick={saveLab} className="w-full btn-teal" data-testid="save-lab-btn">
                Save Lab Result
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Lab Modal */}
      {showViewModal && selectedLab && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{selectedLab.panel_name}</h3>
                <p className="text-sm text-slate-500">
                  {selectedLab.lab_name && `${selectedLab.lab_name} • `}
                  {selectedLab.collection_date || 'Date unknown'}
                </p>
              </div>
              <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 text-sm font-medium text-slate-600">Test</th>
                    <th className="text-left py-2 text-sm font-medium text-slate-600">Value</th>
                    <th className="text-left py-2 text-sm font-medium text-slate-600">Reference</th>
                    <th className="text-left py-2 text-sm font-medium text-slate-600">Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedLab.results?.map((result) => (
                    <tr key={`result-${result.test_name}-${result.value}`} className="border-b border-slate-100">
                      <td className="py-2 text-sm">{result.test_name}</td>
                      <td className="py-2 text-sm font-medium">
                        {result.value} {result.unit}
                      </td>
                      <td className="py-2 text-sm text-slate-500">{result.reference_range || '-'}</td>
                      <td className="py-2">
                        <span className={`text-xs px-2 py-1 rounded ${getFlagColor(result.flag)}`}>
                          {getFlagLabel(result.flag)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {selectedLab.notes && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <h4 className="text-sm font-medium text-slate-700 mb-1">Notes</h4>
                  <p className="text-sm text-slate-600">{selectedLab.notes}</p>
                </div>
              )}
              
              <div className="mt-4 text-xs text-slate-400">
                <p>Status: {selectedLab.status}</p>
                {selectedLab.reviewed_at && <p>Reviewed: {new Date(selectedLab.reviewed_at).toLocaleString()}</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
